import { ZOOM_LEVELS } from '@/constants/zoom';
import { TrackingService } from '@/services/analytics/TrackingService';
import { CONFIG_CONSTANTS, defaultConfig, useConfigStore, buildMenuFromBuildingMenus, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import { useHomeStore } from '@/stores/homeStore';
import type { SystemConfig, languageType } from '@/stores/types/configStore';
import type { GNBItemType } from '@/types';

export interface ResetServiceConfig {
    // Configuration options for the reset service
    isEnabled?: boolean;
    onZoomButtonsClose?: (isOpen: boolean) => void;
}

class KioskResetService {
    private timeoutId: NodeJS.Timeout | null = null;
    private isActive = false;
    private config: ResetServiceConfig = {};
    private unsubscribeStore: (() => void) | null = null;
    private isInitialized = false;
    private eventListeners: Array<{ element: EventTarget; type: string; listener: EventListener }> = [];
    private debug = false;
    private lastInteractionTime = 0;
    private interactionThrottleMs = 1000;

    private log(...args: unknown[]) {
        if (this.debug) {
            // console.log(...args);
            // eslint-disable-next-line no-console
            console.log('[ResetService]', ...args);
        }
    }

    constructor() { }

    private init() {
        if (this.isInitialized || !this.config.isEnabled) return;

        this.log('[ResetService] Initializing service');

        // Subscribe to config store changes
        this.unsubscribeStore = useConfigStore.subscribe((state) => {
            const shouldActivateReset = this.shouldActivateReset(state.config);

            this.log('[ResetService] Config changed, shouldActivateReset:', shouldActivateReset, 'isActive:', this.isActive);

            if (shouldActivateReset && !this.isActive) {
                this.startResetCountdown();
            } else if (!shouldActivateReset && this.isActive) {
                this.stopResetCountdown();
            }
        });

        // Add event listeners for user interactions
        this.setupUserInteractionListeners();

        this.isInitialized = true;
    }

    /**
     * Set up event listeners for user interactions
     */
    private setupUserInteractionListeners() {
        const handleUserInteraction = () => {
            const now = Date.now();
            if (now - this.lastInteractionTime < this.interactionThrottleMs) {
                return;
            }
            this.lastInteractionTime = now;

            const currentConfig = useConfigStore.getState().config;
            const shouldActivateReset = this.shouldActivateReset(currentConfig);

            if (shouldActivateReset) {
                this.log('[ResetService] User interaction detected, restarting countdown');
                this.startResetCountdown();
            }
        };

        const handleKeyDown = (event: Event) => {
            const keyEvent = event as KeyboardEvent;
            const relevantKeys = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'];
            if (relevantKeys.includes(keyEvent.key)) {
                handleUserInteraction();
            }
        };

        const handleClick = () => {
            handleUserInteraction();
        };

        const handleScroll = () => {
            handleUserInteraction();
        };

        // Add event listeners and store references for cleanup
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('click', handleClick);
        document.addEventListener('scroll', handleScroll);

        this.eventListeners.push(
            { element: document, type: 'keydown', listener: handleKeyDown },
            { element: document, type: 'click', listener: handleClick },
            { element: document, type: 'scroll', listener: handleScroll }
        );
    }

    /**
     * Configure the service with callbacks
     */
    configure(config: ResetServiceConfig) {
        const wasEnabled = this.config.isEnabled;
        this.config = config;
        const isNowEnabled = this.config.isEnabled;

        if (!wasEnabled && isNowEnabled) {
            this.log('Service enabled, initializing...');
            this.init();
        } else if (wasEnabled && !isNowEnabled) {
            this.log('Service disabled, destroying...');
            this.destroy();
        } else if (isNowEnabled && this.isInitialized) {
            this.log('Service already enabled, config updated');
        }
    }

    /**
     * Check if reset countdown should be activated based on config changes
     */
    private shouldActivateReset(currentConfig: SystemConfig): boolean {
        const ui = currentConfig.ui;
        const tts = currentConfig.tts;

        // Check all trigger conditions
        const conditions = [
            // 1. Voice setting changed from default when barrier free is enabled
            ui.barrierFree && tts?.voice !== defaultConfig.tts.voice,
            // 2. Volume changed from default
            ui.volume !== defaultConfig.ui.volume,
            // 3. Contrast is enabled
            ui.contrast === true,
            // 4. Zoom is enabled
            ui.zoomIn === true,
            // 5. Screen mode is not fullscreen
            ui.screenMode !== 'fullscreen',
        ];

        // Debug logging
        this.log('[ResetService] Checking conditions:', {
            'barrierFree && voice changed': ui.barrierFree && tts?.voice !== defaultConfig.tts.voice,
            'volume changed': ui.volume !== defaultConfig.ui.volume,
            'contrast enabled': ui.contrast === true,
            'zoom enabled': ui.zoomIn === true,
            'screen mode not fullscreen': ui.screenMode !== 'fullscreen',
            'current values': {
                barrierFree: ui.barrierFree,
                voice: tts?.voice,
                volume: ui.volume,
                contrast: ui.contrast,
                zoomIn: ui.zoomIn,
                screenMode: ui.screenMode,
            },
            'default values': {
                voice: defaultConfig.tts.voice,
                volume: defaultConfig.ui.volume,
                contrast: defaultConfig.ui.contrast,
                zoomIn: defaultConfig.ui.zoomIn,
                screenMode: defaultConfig.ui.screenMode,
            },
        });

        const result = conditions.some((condition) => condition === true);
        this.log('[ResetService] shouldActivateReset result:', result);

        return result;
    }

    /**
     * Start the reset countdown
     */
    startResetCountdown() {
        // Clear existing countdown if running
        if (this.isActive) {
            // this.log('[ResetService] Restarting existing countdown');
            this.stopResetCountdown();
            TrackingService.onHomePopupClose();
        }
        TrackingService.onHomePopupOpen();

        const currentConfig = useConfigStore.getState().config;
        const timeoutSeconds = currentConfig.ui.idleTimeoutSec || 180;

        this.log(`[ResetService] Starting reset countdown for ${timeoutSeconds} seconds`);

        this.isActive = true;

        this.timeoutId = setTimeout(() => {
            // Check if TTS is enabled with headphone speaker - don't reset in this case
            const configAtTimeout = useConfigStore.getState().config;
            const shouldSkipReset = configAtTimeout?.tts?.voice === true && configAtTimeout?.tts?.speaker === 'headphone';

            if (shouldSkipReset) {
                this.log('[ResetService] Skipping reset - TTS is enabled with headphone speaker');
                this.isActive = false;
                this.timeoutId = null;
                return;
            }

            this.executeReset();
        }, timeoutSeconds * 1000);
    }

    /**
     * Stop the reset countdown
     */
    stopResetCountdown() {
        if (!this.isActive) return;

        this.log('[ResetService] Stopping reset countdown');

        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        this.isActive = false;
    }

    triggerResetCountdown() {
        this.log('[ResetService] Manually triggering reset countdown');
        // Always start countdown when manually triggered, regardless of current state
        const currentConfig = useConfigStore.getState().config;
        const shouldActivateReset = this.shouldActivateReset(currentConfig);

        if (shouldActivateReset) {
            this.startResetCountdown();
        } else {
            this.log('[ResetService] Manual trigger ignored - no trigger conditions met');
        }
    }

    public executeReset() {
        this.log('[ResetService] Executing reset to default configuration');

        const { updateConfig } = useConfigStore.getState();
        const currentConfig = useConfigStore.getState().config;
        const buildingMenus = currentConfig.server?.buildingMenus;
        const lotteEvents = currentConfig.server?.building?.lotteEvents;
        const currentLanguage = (currentConfig.server?.store?.storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        // Build default menu using buildMenuFromBuildingMenus
        const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, currentLanguage, lotteEvents);

        // Reset to default configuration
        setTimeout(() => {
            updateConfig({
                ui: {
                    ...currentConfig.ui,
                    language: currentLanguage,
                    menu,
                    requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, currentLanguage),
                    volume: defaultConfig.ui.volume,
                    contrast: defaultConfig.ui.contrast,
                    zoomIn: defaultConfig.ui.zoomIn,
                    zoomLevel: ZOOM_LEVELS[0],
                    zoomClosed: defaultConfig.ui.zoomClosed,
                    screenMode: defaultConfig.ui.screenMode,
                },
                tts: {
                    ...currentConfig.tts,
                    speaker: defaultConfig.tts.speaker,
                    voice: defaultConfig.tts.voice,
                },
            });

            // Execute callbacks
            this.config?.onZoomButtonsClose?.(false);

            // Track the reset event - this will save session if one exists
            TrackingService.onHomePopupClose();
            TrackingService.onTimeoutNavigateHome();
            useHomeStore.getState().resetHomeMenu();

            this.stopResetCountdown();
        }, 100);
    }

    /**
     * Check if reset countdown is currently active
     */
    isResetActive(): boolean {
        return this.isActive;
    }

    /**
     * Cleanup the service
     */
    destroy() {
        this.stopResetCountdown();

        // Unsubscribe from store
        if (this.unsubscribeStore) {
            this.unsubscribeStore();
            this.unsubscribeStore = null;
        }

        // Remove event listeners
        this.eventListeners.forEach(({ element, type, listener }) => {
            element.removeEventListener(type, listener);
        });
        this.eventListeners = [];

        this.isInitialized = false;
    }
}

// Create singleton instance
export const resetService = new KioskResetService();

export default resetService;
